import type { HotelSearchValue } from '../models.js';
import type { HotelDateRange } from './hotel_date_calendar.js';
import type { HotelSearchBarProps } from './hotel_search_bar.js';
import type { HotelSearchPanels } from './hotel_search_panels.js';
import { pendingHotelSearchFields } from './hotel_search_pending.js';

/*
 * O Confirmar das datas da barra de hotel e a régua de "pode buscar" que ele e
 * o Buscar compartilham. Saiu de `hotel_search_bar.tsx` pelo teto de linhas
 * dele; a barra é a única consumidora.
 */

/** O host aceita buscar `value` (ver `HotelSearchBarProps.canSubmit`). */
function canSearch(props: HotelSearchBarProps, value: HotelSearchValue): boolean {
  return props.canSubmit?.(value) ?? true;
}

/**
 * As props do Buscar (#5085), o `searchCta` do `useGuidedFlow` nos painéis do
 * hotel: com a busca recusada e um campo VAZIO, no guia, o clique abre o painel
 * do primeiro pendente — o caminho do `afterPick` — em vez de ficar inerte sem
 * dizer o quê. A régua do host é mais larga que "campo vazio" (o parse da
 * seleção): sem campo vazio, ou fora do guia, o Buscar segue travado.
 */
export function searchCta(
  props: HotelSearchBarProps,
  panels: Pick<HotelSearchPanels, 'guided' | 'setRequestedPanel'>,
  disabled: boolean,
  submit: () => void,
): { readonly disabled: boolean; readonly onClick: () => void } {
  if (disabled || canSearch(props, props.value)) return { disabled, onClick: submit };
  const ask = panels.guided ? pendingHotelSearchFields(props.value)[0] : undefined;
  if (!ask) return { disabled: true, onClick: submit };
  return {
    disabled: false,
    onClick: () => {
      panels.setRequestedPanel(ask);
    },
  };
}

/** O Confirmar das datas BUSCA (o rótulo diz): não trava, não é turno, o ONDE
 *  está dado e o host aceita o valor com o RASCUNHO das datas — o mesmo que o
 *  Confirmar entrega (`useStayConfirm`). */
export function staySearches(
  props: HotelSearchBarProps,
  disabled: boolean,
  dates: HotelDateRange,
): boolean {
  const { value } = props;
  if (disabled || props.submitIsTurn === true || !value.place.trim()) return false;
  return canSearch(props, { ...value, ...dates });
}

/**
 * O Confirmar das datas — o mesmo nos dois palcos, na régua do `useGuidedFlow`
 * (#4620): no guia, com o ONDE vazio, ele abre o ONDE; senão é a BUSCA, com o
 * valor novo (`onSubmit(next)`) — salvo no card superado (`submitIsTurn`:
 * enviar mensagem é gesto para fora e não sai de confirmar um campo) ou com o
 * veto do host (`canSubmit`), quando no guia abre a próxima pergunta (o QUEM,
 * o único campo que o calendário não responde). A vaga sem hóspede NÃO segura
 * a busca: a régua de "pode buscar" é do host.
 */
export function useStayConfirm(
  props: HotelSearchBarProps,
  panels: HotelSearchPanels,
  disabled: boolean,
): (dates: HotelDateRange) => void {
  return (dates) => {
    const next = { ...props.value, ...dates };
    props.onChange(next);
    if (panels.guided && pendingHotelSearchFields(next).includes('places')) {
      panels.afterPick(next, () => {});
      return;
    }
    panels.setRequestedPanel(null);
    if (!disabled && props.submitIsTurn !== true && canSearch(props, next)) {
      props.onSubmit(next);
      panels.focusSearch();
      return;
    }
    // Sem busca: no card superado o foco vai ao Buscar (que faz o turno); sem
    // o que buscar, fica no gatilho das datas — e o guia abre o QUEM.
    if (!disabled && props.submitIsTurn === true) panels.focusSearch();
    else panels.datesTriggerRef.current?.focus();
    if (panels.guided) panels.afterPick(next, () => {});
  };
}
